import React, { useEffect } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Space, Button, Typography, message } from "@mimber/ui-web"
import { Form, SelectField, DatePickerField, InputField } from "@mimber/forms"
import {
  useInvoice,
  useRecordInvoicePayment,
} from "@mimber/client/mimber-organization"
import { PaymentMethod } from "@mimber/schema"
import { formatCurrency } from "@/utils"
import type { RecordPaymentFormProps } from "./RecordPaymentForm.types"
import {
  recordPaymentFormSchema,
  type RecordPaymentFormData,
} from "./RecordPaymentForm.schema"
import { PAYMENT_METHOD_OPTIONS } from "./RecordPaymentForm.utils"

export const RecordPaymentForm: React.FunctionComponent<
  RecordPaymentFormProps
> = ({ invoiceUuid, onSuccess }) => {
  const form = useForm<RecordPaymentFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(recordPaymentFormSchema) as any,
    mode: "onBlur",
    defaultValues: {
      amount: 0,
      method: PaymentMethod.CASH,
      paidAt: "",
      note: "",
    },
  })

  const { reset } = form
  const { data: invoiceResult, isLoading: isInvoiceLoading } =
    useInvoice(invoiceUuid)

  const invoiceAmountCents =
    invoiceResult?.data.memberPaysTotal ??
    invoiceResult?.data.amountDue ??
    invoiceResult?.data.totalAmount ??
    0

  useEffect(() => {
    if (!invoiceResult?.data) {
      return
    }

    reset((currentValues) => ({
      ...currentValues,
      amount: invoiceAmountCents,
    }))
  }, [invoiceAmountCents, invoiceResult?.data, reset])

  const mutation = useRecordInvoicePayment(invoiceUuid, {
    onSuccess: () => {
      message.success("Payment recorded successfully")
      reset({
        amount: invoiceAmountCents,
        method: PaymentMethod.CASH,
        paidAt: "",
        note: "",
      })
      onSuccess?.()
    },
    onError: (error) => {
      message.error(`Failed to record payment: ${error.message}`)
    },
  })

  const onSubmit = (data: RecordPaymentFormData) => {
    if (!invoiceResult?.data) {
      return
    }

    mutation.mutate({
      amount: invoiceAmountCents,
      method: data.method,
      paidAt: data.paidAt || undefined,
      note: data.note || undefined,
    })
  }

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      disabled={mutation.isPending || isInvoiceLoading || !invoiceResult?.data}
      layout="vertical"
    >
      <Space
        orientation="vertical"
        size={4}
        style={{ width: "100%", marginBottom: 16 }}
      >
        <Typography.Text strong>Amount</Typography.Text>
        <Typography.Text>
          {invoiceResult?.data
            ? formatCurrency(invoiceAmountCents)
            : "Loading invoice amount..."}
        </Typography.Text>
        <Typography.Text type="secondary">
          Partial payments are not supported yet.
        </Typography.Text>
      </Space>

      <SelectField
        name="method"
        label="Payment Method"
        placeholder="Select payment method"
        options={PAYMENT_METHOD_OPTIONS}
        required
      />

      <DatePickerField
        name="paidAt"
        label="Payment Date (optional)"
        placeholder="Defaults to today"
      />

      <InputField
        name="note"
        label="Note (optional)"
        placeholder="Any additional notes"
        multiline
        rows={3}
      />

      <Space style={{ justifyContent: "flex-end", width: "100%" }}>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>
          Record Payment
        </Button>
      </Space>
    </Form>
  )
}
